import { useEffect, useState } from "react";
import { useFields } from "../../hooks/queries";
import { LessonPlanCheck } from "./LessonPlanCheck";
import { WizardFinale } from "./WizardFinale";
import { WizardStepCard } from "./WizardStepCard";
import { wizardWidgetPos } from "./wizardPlacement";
import { useFieldToolsStore } from "../../stores/fieldToolsStore";
import { useRxEdit } from "../../stores/rxEditStore";
import { useUiStore } from "../../stores/uiStore";
import { useWizard } from "./wizardStore";
import { WizardNotice } from "./WizardNotice";

/*
 * the anchor's viewport rect, re-resolved on an interval — anchors mount late (async layers)
 * move, and rect-provider anchors track the map camera
 */
type Rectish = {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};
function useAnchorRect(
  anchor: (() => HTMLElement | Rectish | null) | undefined,
  step: number,
): Rectish | null {
  const [rect, setRect] = useState<Rectish | null>(null);
  useEffect(() => {
    setRect(null);
    if (!anchor) return;
    const read = () => {
      const got = anchor();
      const r =
        got == null
          ? null
          : got instanceof HTMLElement
            ? got.getBoundingClientRect()
            : got;
      setRect((cur) => {
        if (!r) return cur == null ? cur : null;
        if (
          cur &&
          Math.abs(cur.left - r.left) < 1 &&
          Math.abs(cur.top - r.top) < 1 &&
          Math.abs(cur.width - r.width) < 1
        )
          return cur;
        return r;
      });
    };
    read();
    const t = window.setInterval(read, 300);
    window.addEventListener("resize", read);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("resize", read);
    };
  }, [anchor, step]);
  return rect;
}

export function WizardHost() {
  const wizard = useWizard((s) => s.wizard);
  const step = useWizard((s) => s.step);
  const fieldId = useWizard((s) => s.fieldId);
  const notice = useWizard((s) => s.notice);
  const mapOpen = useUiStore((s) => s.mapOpen);
  const { data: fields } = useFields();
  const cur = wizard?.steps[step] ?? null;

  // THE TOUR IS MAP-SCOPED for map steps: closing the map ends it.
  const needsMap =
    cur == null || cur.scene == null || (cur.scene.view ?? "map") === "map";
  useEffect(() => {
    if (wizard && !mapOpen && needsMap) useWizard.getState().exit();
  }, [wizard, mapOpen, needsMap]);

  // ---- the DASHBOARD scene reconciler (steps 17+): the map driver unmounts with the map, so the
  // host — mounted app-wide — realizes these: close the map, end the Rx session the tour opened
  // open the Spread panel and its editor. Registry misses self-heal via actionsVersion.
  const actionsVersion = useWizard((s) => s.actionsVersion);
  const ftAction = useFieldToolsStore((s) => s.action); // observed: the reconciler re-runs if the user closes the panel
  useEffect(() => {
    const scene = cur?.scene;
    if (!wizard || !scene || scene.view !== "dashboard") return;
    if (mapOpen) useUiStore.getState().closeMap();
    if (scene.rxEditor === null && useRxEdit.getState().fieldId)
      useRxEdit.getState().end();
    if (scene.spread) {
      const ft = useFieldToolsStore.getState();
      // “The same field selected” — A, steps 17–19 — with several fields selected the panel
      // heads TABLE order while the tour ran on click order: narrow the selection to the tour's
      // field so the spread cards show the Rx the tour just saved.
      const sel = ft.rowSelection;
      if (
        fieldId &&
        (!sel[fieldId] || Object.keys(sel).filter((k) => sel[k]).length !== 1)
      ) {
        useFieldToolsStore.setState({ rowSelection: { [fieldId]: true } });
      }
      if (ft.action !== "spread") ft.toggleAction("spread");
      useWizard.getState().invokeAction("spread.edit", scene.spread.editing);
    }
  }, [wizard, cur, fieldId, mapOpen, actionsVersion, ftAction]);

  // Capture phase so a suppressed click never reaches the platform; passthrough lets the native
  // action run (the next scene builds on it).
  useEffect(() => {
    const adv = cur?.advance;
    if (!adv) return;
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !adv.match(el)) return;
      if (!adv.passthrough) {
        e.preventDefault();
        e.stopPropagation();
      }
      cur?.onAdvance?.(); // the step's side effect (step 16 saves the Rx) — before the step changes
      // advance AFTER the native click dispatches (or is swallowed), never re-entrantly
      queueMicrotask(() => useWizard.getState().next());
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [cur]);

  const rect = useAnchorRect(cur?.anchor, step);
  // the pulsing ring stays on the CLICK TARGET even when the widget's anchor moved elsewhere
  const hlRect = useAnchorRect(cur?.highlight, step);

  if (notice) return <WizardNotice notice={notice} />;
  if (!wizard || !cur || !fieldId) return null;
  if (cur.scene === null)
    return <LessonPlanCheck key={fieldId} fieldId={fieldId} />;

  const fieldName =
    fields?.find((f) => f.field_id === fieldId)?.field_name ?? "this field";
  const pos = wizardWidgetPos(rect, cur.side, cur.center);

  // THE FINALE: a bigger, attention-getting card — the robot's encore.
  if (cur.finale) {
    return (
      <WizardFinale
        step={step}
        lastStep={wizard.steps.length - 1}
        text={cur.text({ fieldName })}
      />
    );
  }

  return (
    <WizardStepCard
      step={step}
      lastStep={wizard.steps.length - 1}
      text={cur.text({ fieldName })}
      showNext={cur.next}
      onAdvance={cur.onAdvance}
      pos={pos}
      ring={hlRect ?? rect}
    />
  );
}
